import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw, Info, AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import {
  fetchEditCompanyDetailsAction,
  editCompanyAction,
} from "../../slices/companiesSlice";
import { useNotification } from "@/context/NotificationContext";
import {
  editCompanySchema,
  type EditCompanyFormData,
} from "../../schemas/companySchemas";
import { EditCompanyDTO } from "@/api/editCompany/types";
import { SoftwareSection } from "../SoftwareSection/SoftwareSection";
import ServiceTitanIntegration from "../ServiceTitanIntegration/ServiceTitanIntegration";
import CompanyEmployeeList from "../CompanyEmployeeList/CompanyEmployeeList";
import CompanyPermissionsCard from "../CompanyPermissionsCard/CompanyPermissionsCard";
import CompanySubscriptionAccess from "../CompanySubscriptionAccess/CompanySubscriptionAccess";
import { resyncCompanyFromSalesforce } from "@/api/resyncCompanyFromSalesforce";

// Radix Select cannot use "" as a SelectItem value. Use a sentinel for the
// "Not Set" option and translate to literal null on submit.
const LEAD_GEN_PAYMENT_NULL_SENTINEL = "__not_set__";

const EditCompany: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, selectedCompany } = useSelector(
    (state: RootState) => state.companies,
  );
  const { showNotification } = useNotification();
  const [resyncing, setResyncing] = useState(false);
  // Bumped on every successful save so the Permissions card refetches —
  // a save may change the stage, which changes the inherited permissions.
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);

  const form = useForm<EditCompanyFormData>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      fieldServiceSoftwareId: null,
      leadGenPaymentSourceType: null,
      leadGenCrid: null,
      stageUuid: null,
    },
    mode: "onChange",
  });

  const { handleSubmit, formState, reset, control } = form;
  const { isSubmitting, isValid, isDirty } = formState;

  // Watch the fieldServiceSoftwareId to determine if ServiceTitan section should be shown
  const selectedSoftwareId = useWatch({
    control,
    name: "fieldServiceSoftwareId",
  });

  // Determine if ServiceTitan is selected
  const isServiceTitanSelected = useMemo(() => {
    if (!selectedSoftwareId || !selectedCompany?.fieldServiceSoftwareList) {
      return false;
    }
    const selectedSoftware = selectedCompany.fieldServiceSoftwareList.find(
      (software) => software.id === selectedSoftwareId,
    );
    return selectedSoftware?.name === "ServiceTitan";
  }, [selectedSoftwareId, selectedCompany?.fieldServiceSoftwareList]);

  useEffect(() => {
    if (uuid) {
      dispatch(fetchEditCompanyDetailsAction(uuid));
    }
  }, [dispatch, uuid]);

  useEffect(() => {
    if (selectedCompany) {
      reset({
        fieldServiceSoftwareId: selectedCompany.fieldServiceSoftwareId || null,
        leadGenPaymentSourceType:
          selectedCompany.leadGenPaymentSourceType ?? null,
        leadGenCrid: selectedCompany.leadGenCrid ?? null,
        stageUuid: selectedCompany.stage?.uuid ?? null,
      });
    }
  }, [selectedCompany, reset]);

  const handleResync = async () => {
    if (!uuid) return;

    try {
      setResyncing(true);
      await resyncCompanyFromSalesforce(uuid);
      showNotification(
        "Resync Complete",
        "Company and employee data has been refreshed from Salesforce.",
        "success",
      );
      // Refresh the page data
      dispatch(fetchEditCompanyDetailsAction(uuid));
    } catch {
      showNotification(
        "Resync Failed",
        "Failed to resync company from Salesforce. Please try again.",
        "error",
      );
    } finally {
      setResyncing(false);
    }
  };

  const onSubmit = async (values: EditCompanyFormData) => {
    if (!uuid) return;

    try {
      const editCompanyDTO: EditCompanyDTO = {
        fieldServiceSoftwareId: values.fieldServiceSoftwareId ?? null,
        leadGenPaymentSourceType: values.leadGenPaymentSourceType ?? null,
        leadGenCrid: values.leadGenCrid ?? null,
        stageUuid: values.stageUuid ?? null,
      };

      await dispatch(
        editCompanyAction(uuid, editCompanyDTO, () => {
          setPermissionsRefreshKey((key) => key + 1);
        }),
      );

      showNotification(
        "Company Updated",
        "Company settings have been saved successfully.",
        "success",
      );

      reset({
        fieldServiceSoftwareId: values.fieldServiceSoftwareId,
        leadGenPaymentSourceType: values.leadGenPaymentSourceType ?? null,
        leadGenCrid: values.leadGenCrid ?? null,
        stageUuid: values.stageUuid ?? null,
      });
    } catch {
      showNotification(
        "Update Failed",
        "Failed to update company settings. Please try again.",
        "error",
      );
    }
  };

  // Format address for display
  const formatAddress = (
    street: string | null | undefined,
    city: string | null | undefined,
    state: string | null | undefined,
    postalCode: string | null | undefined,
    country: string | null | undefined,
  ) => {
    const parts = [street, city, state, postalCode, country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  const employeesWithoutRoles = useMemo(() => {
    if (!selectedCompany?.employees) return [];
    return selectedCompany.employees.filter((emp) => !emp.businessRoleId);
  }, [selectedCompany?.employees]);

  if (!uuid) {
    return null;
  }

  return (
    <MainPageWrapper
      error={error}
      loading={loading || resyncing}
      title="Edit Company"
    >
      {employeesWithoutRoles.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                {employeesWithoutRoles.length}{" "}
                {employeesWithoutRoles.length === 1
                  ? "employee has"
                  : "employees have"}{" "}
                no business role assigned
              </p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300/80">
                Employees must have a business role assigned before they can be
                synced to Auth0, Member Portal, Partner Network, or Scoreboard.{" "}
                <button
                  className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
                  onClick={() =>
                    document
                      .getElementById("employees-section")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  type="button"
                >
                  View employees
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Salesforce Data Card (Read-Only) */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Salesforce Data</CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Info className="h-3 w-3" />
              These fields are synced from Salesforce and cannot be edited
              directly.
            </CardDescription>
          </div>
          <Button
            disabled={resyncing || loading}
            onClick={handleResync}
            size="sm"
            variant="outline"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${resyncing ? "animate-spin" : ""}`}
            />
            {resyncing ? "Resyncing..." : "Resync from Salesforce"}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Company Identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Company Name
                </label>
                <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                  {selectedCompany?.companyName || "—"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Company Email
                </label>
                <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                  {selectedCompany?.companyEmail || "—"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Website
                </label>
                <p className="text-sm mt-1">
                  {selectedCompany?.websiteUrl ? (
                    <a
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      href={selectedCompany.websiteUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {selectedCompany.websiteUrl}
                    </a>
                  ) : (
                    <span className="text-gray-900 dark:text-slate-100">—</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Phone
                </label>
                <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                  {selectedCompany?.phoneNumber || "—"}
                </p>
              </div>
            </div>

            {/* IDs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Salesforce ID
                </label>
                <p className="text-sm mt-1 font-mono text-gray-900 dark:text-slate-100">
                  {selectedCompany?.salesforceId || "—"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Intacct ID
                </label>
                <p className="text-sm mt-1 font-mono text-gray-900 dark:text-slate-100">
                  {selectedCompany?.intacctId || "—"}
                </p>
              </div>
            </div>

            {/* Trade Access */}
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                Trades
              </label>
              <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                {selectedCompany?.tradeNames &&
                selectedCompany.tradeNames.length > 0
                  ? selectedCompany.tradeNames.join(", ")
                  : "No trades assigned"}
                {selectedCompany?.primaryTradeName && (
                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded-full">
                    Primary: {selectedCompany.primaryTradeName}
                  </span>
                )}
              </p>
            </div>

            {/* Access Flags */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Marketing Enabled
                </label>
                <p className="text-sm mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedCompany?.marketingEnabled
                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                        : "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300"
                    }`}
                  >
                    {selectedCompany?.marketingEnabled ? "Yes" : "No"}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Eagles Access
                </label>
                <p className="text-sm mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedCompany?.hasEaglesAccess
                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                        : "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300"
                    }`}
                  >
                    {selectedCompany?.hasEaglesAccess ? "Yes" : "No"}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  LATA Access
                </label>
                <p className="text-sm mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedCompany?.hasLATAAccess
                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                        : "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300"
                    }`}
                  >
                    {selectedCompany?.hasLATAAccess ? "Yes" : "No"}
                  </span>
                </p>
              </div>
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Billing Address
                </label>
                <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                  {formatAddress(
                    selectedCompany?.billingStreet,
                    selectedCompany?.billingCity,
                    selectedCompany?.billingState,
                    selectedCompany?.billingPostalCode,
                    selectedCompany?.billingCountry,
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-slate-400">
                  Shipping Address
                </label>
                <p className="text-sm mt-1 text-gray-900 dark:text-slate-100">
                  {formatAddress(
                    selectedCompany?.shippingStreet,
                    selectedCompany?.shippingCity,
                    selectedCompany?.shippingState,
                    selectedCompany?.shippingPostalCode,
                    selectedCompany?.shippingCountry,
                  )}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Access (Total Access) - delinquency + admin override */}
      {selectedCompany && uuid && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Subscription Access</CardTitle>
            <CardDescription>
              Total Access and other subscription entitlements, their billing
              status, and admin overrides. Delinquent subscriptions lose access
              automatically unless an override is applied.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CompanySubscriptionAccess companyUuid={uuid} />
          </CardContent>
        </Card>
      )}

      {/* Hub Configuration Card (Editable) */}
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Hub Configuration</CardTitle>
              <CardDescription>
                Settings managed within the Hub application.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SoftwareSection
                fieldServiceSoftwareList={
                  selectedCompany?.fieldServiceSoftwareList || []
                }
                form={form}
              />
              <div className="mt-6">
                <FormField
                  control={control}
                  name="stageUuid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage</FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(value === "none" ? null : value)
                        }
                        value={field.value ?? "none"}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">
                            No Stage Selected
                          </SelectItem>
                          {(selectedCompany?.stageList || []).map((stage) => (
                            <SelectItem key={stage.uuid} value={stage.uuid}>
                              {stage.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        The stage this company is currently in.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-6">
                <FormField
                  control={control}
                  name="leadGenPaymentSourceType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="leadGenPaymentSourceType">
                        Default LeadGen Payment Source
                      </FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(
                            value === LEAD_GEN_PAYMENT_NULL_SENTINEL
                              ? null
                              : (value as "ach" | "card"),
                          )
                        }
                        value={field.value ?? LEAD_GEN_PAYMENT_NULL_SENTINEL}
                      >
                        <FormControl>
                          <SelectTrigger
                            aria-label="Default LeadGen Payment Source"
                            id="leadGenPaymentSourceType"
                          >
                            <SelectValue placeholder="— Not Set —" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={LEAD_GEN_PAYMENT_NULL_SENTINEL}>
                            — Not Set —
                          </SelectItem>
                          <SelectItem value="ach">ACH</SelectItem>
                          <SelectItem value="card">Credit Card</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Used when CARA has no payment method on file. CARA
                        always wins when it has data.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-6">
                <FormField
                  control={control}
                  name="leadGenCrid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="leadGenCrid">LeadGen CRID</FormLabel>
                      <FormControl>
                        <Input
                          id="leadGenCrid"
                          name={field.name}
                          onBlur={field.onBlur}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === "" ? null : e.target.value,
                            )
                          }
                          placeholder="— Not Set —"
                          ref={field.ref}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormDescription>
                        Customer Registration ID used for LeadGen billing.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              disabled={isSubmitting}
              onClick={() => navigate("/admin/companies")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid || !isDirty || isSubmitting}
              type="submit"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>

      {/* Permissions - stage-inherited + company overrides */}
      {selectedCompany && uuid && (
        <CompanyPermissionsCard
          companyUuid={uuid}
          refreshKey={permissionsRefreshKey}
        />
      )}

      {/* Company Employees */}
      {selectedCompany?.employees && selectedCompany?.businessRoles && (
        <Card className="mt-6" id="employees-section">
          <CardHeader>
            <CardTitle>Employees</CardTitle>
            <CardDescription>
              Assign business roles to company employees.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CompanyEmployeeList
              businessRoles={selectedCompany.businessRoles}
              employees={selectedCompany.employees}
            />
          </CardContent>
        </Card>
      )}

      {/* ServiceTitan Integration - Separate from main form */}
      {selectedCompany && uuid && isServiceTitanSelected && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>ServiceTitan Integration</CardTitle>
            <CardDescription>
              Configure ServiceTitan credentials and map business units to
              trades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ServiceTitanIntegration
              companyId={selectedCompany.id}
              companyUuid={uuid}
            />
          </CardContent>
        </Card>
      )}
    </MainPageWrapper>
  );
};

export default EditCompany;
